// React
import { useMemo } from "react"

// Translations
import { useTrans } from "@/i18n"
import { useOnlyCoreLocationCountryChoices } from "@/lib/countries"

// UI
import { useToasts } from "@/context/toasts"
import { Heading } from "../Typography"
import { CardBody, CardFooter, CardWrapper } from "../Card"
import { Tooltip } from "../Tooltip"

// Icons
import { FiChevronDown, FiInfo } from "@/lib/icons"

// Forms
import { FormikProvider, useFormik } from "formik"
import { handleErrorWithFormik } from "@/lib/formik"
import { FormikError } from "../form-controls/Errors"
import { FormGroup } from "../form-controls/FormGroup"
import {
	FormikErrors,
	FormikInput,
	FormikLabel,
	FormikSubmitButton,
} from "../form-controls/formik"
import { Select, SelectButton } from "../form-controls"

// GraphQL
import {
	useCompanyDetailsQueryQuery,
	useCompanyDetailsMutationMutation,
	CoreLocationCountryChoices,
	CoreLocationRegionChoices,
} from "@/api/graphql"

// Types
type FormValues = {
	id: string
	business_legal_name?: string | null
	business_nr?: string | null
	location?: {
		address_line_primary: string
		address_line_secondary?: string
		city?: string
		country?: CoreLocationCountryChoices
		postal_code?: string
		region?: CoreLocationRegionChoices
	} | null
}

/**
 * CompanyDetails
 * Shows and allows editing of company details for a specific organization
 *
 * @returns
 */
export const CompanyDetails = ({
	selectedOrganisationId,
}: {
	selectedOrganisationId: string | undefined
}) => {
	const toasts = useToasts()

	// Translations
	const t = useTrans("profile")
	const onlyCoreLocationCountryChoices = useOnlyCoreLocationCountryChoices()

	// GraphQL
	const { data, refetch } = useCompanyDetailsQueryQuery()

	// Find the selected organization based on the selectedOrganisationId
	const selectedOrganization = useMemo(() => {
		return data?.me?.organisations?.find(
			(org) => org?.id === selectedOrganisationId,
		)
	}, [data, selectedOrganisationId])

	const updateCompanyDetails = useCompanyDetailsMutationMutation({
		onSuccess: async (response) => {
			// map each error from graphql to the field it's related to
			for (const error of response?.organisation_update?.errors ?? []) {
				if (error?.field) {
					form.setFieldError(error.field, error.messages[0])
				}
			}

			// do we have data? then success
			if (
				!response?.organisation_update?.errors ||
				response.organisation_update.errors?.length === 0
			) {
				toasts.addToast({
					variant: "success",
					id: `bank-details-success-${Date.now()}`,
					text: t(`profile.personal_details.success_message`),
				})
				await refetch()
			}
			// otherwise show a message that there are form errors
			else {
				form.setFieldError(
					"errors.common",
					"common.form_errors.attention_required",
				)
			}
		},
		onError: (error: Error) => {
			handleErrorWithFormik(error, form)
		},
	})

	// Form
	const form = useFormik<FormValues>({
		initialValues: selectedOrganization ?? {
			id: "",
			business_legal_name: "",
			business_nr: "",
			location: {
				address_line_primary: "",
				address_line_secondary: "",
				city: "",
				country: "" as CoreLocationCountryChoices,
				postal_code: "",
			},
		},
		enableReinitialize: true,
		onSubmit: async (values) => {
			await updateCompanyDetails.mutateAsync({
				input: {
					id: values.id,
					location: {
						address_line_primary:
							values.location?.address_line_primary ?? "",
						address_line_secondary:
							values.location?.address_line_secondary ?? "",
						city: values.location?.city ?? "",
						country:
							(values.location
								?.country as CoreLocationCountryChoices) ?? "",
						postal_code: values.location?.postal_code ?? "",
					},
				},
			})
		},
	})

	return (
		<FormikProvider value={form}>
			<CardWrapper>
				<form onSubmit={form.handleSubmit}>
					<CardBody>
						<Heading
							as="h2"
							styleAs="h5"
							className="mb-3 sm:truncate"
						>
							{t("profile:profile.company_details.title")}
						</Heading>
						<FormGroup
							heading={
								<p>
									{t("profile:profile.company_details.copy")}
								</p>
							}
						>
							<div className="space-y-6">
								{/** Organisation name */}
								<div>
									<FormikLabel
										htmlFor="business_legal_name"
										className="flex"
									>
										<Tooltip
											content={
												<p>
													{t(
														"profile:profile.company_details.organisation_name.tooltip",
													)}
												</p>
											}
										>
											<span className="flex items-center">
												{t(
													"profile:profile.company_details.organisation_name.title",
												)}
												<FiInfo className="ml-2" />
											</span>
										</Tooltip>
									</FormikLabel>
									<FormikInput
										name="business_legal_name"
										value={
											form.values.business_legal_name ??
											""
										}
										type="text"
										disabled
										autoComplete="organization"
										className="block w-full"
										classNameWrapper="lg:w-1/2"
										aria-label={t(
											"profile:profile.company_details.organisation_name.title",
										)}
									/>
									<FormikError
										field="organisationName"
										namespace="profile"
									/>
								</div>

								{/** Chamber of Commerce number */}
								<div>
									<FormikLabel
										htmlFor="business_nr"
										className="flex"
									>
										<Tooltip
											content={
												<p>
													{t(
														"profile:profile.company_details.chamber_of_commerce_number.tooltip",
													)}
												</p>
											}
										>
											<span className="flex items-center">
												{t(
													"profile:profile.company_details.chamber_of_commerce_number.title",
												)}
												<FiInfo className="ml-2" />
											</span>
										</Tooltip>
									</FormikLabel>
									<FormikInput
										name="business_nr"
										value={form.values.business_nr ?? ""}
										type="text"
										disabled
										autoComplete="organization"
										className="block w-full"
										classNameWrapper="lg:w-1/2"
										aria-label={t(
											"profile:profile.company_details.chamber_of_commerce_number.title",
										)}
									/>
									<FormikError
										field="chamberOfCommerceNumber"
										namespace="profile"
									/>
								</div>

								{/** Street name */}
								<div>
									<FormikLabel htmlFor="location.address_line_primary">
										{t(
											"profile:profile.personal_details.streetname.title",
										)}
									</FormikLabel>
									<FormikInput
										name="location.address_line_primary"
										value={
											form.values.location
												?.address_line_primary ?? ""
										}
										type="text"
										required
										autoComplete="organization"
										className="block w-full"
										classNameWrapper="lg:w-1/2"
										aria-label={t(
											"profile:profile.personal_details.streetname.title",
										)}
									/>
									<FormikError
										field="streetName"
										namespace="profile"
									/>
								</div>

								{/** Housenumber + suffix */}
								<div>
									<FormikLabel
										htmlFor="location.address_line_secondary"
										className="truncate"
									>
										{t(
											"profile:profile.personal_details.house_number.title",
										)}
									</FormikLabel>
									<FormikInput
										name="location.address_line_secondary"
										value={
											form.values.location
												?.address_line_secondary ?? ""
										}
										type="text"
										required
										autoComplete="organization"
										className="block w-full"
										classNameWrapper="lg:w-1/2"
										aria-label={t(
											"profile:profile.personal_details.house_number.title",
										)}
									/>
									<FormikError
										field="houseNumber"
										namespace="profile"
									/>
								</div>

								{/** Postal code */}
								<div>
									<FormikLabel htmlFor="location.postal_code">
										{t(
											"profile:profile.personal_details.postcode.title",
										)}
									</FormikLabel>
									<FormikInput
										name="location.postal_code"
										value={
											form.values.location?.postal_code ??
											""
										}
										type="text"
										required
										autoComplete="organization"
										className="block w-full"
										classNameWrapper="lg:w-1/2"
										aria-label={t(
											"profile:profile.personal_details.postcode.title",
										)}
									/>
									<FormikError
										field="postalCode"
										namespace="profile"
									/>
								</div>

								{/** City */}
								<div>
									<FormikLabel htmlFor="location.city">
										{t(
											"profile:profile.personal_details.city.title",
										)}
									</FormikLabel>
									<FormikInput
										name="location.city"
										value={form.values.location?.city ?? ""}
										type="text"
										required
										autoComplete="organization"
										className="block w-full"
										classNameWrapper="lg:w-1/2"
										aria-label={t(
											"profile:profile.personal_details.city.title",
										)}
									/>
									<FormikError
										field="city"
										namespace="profile"
									/>
								</div>

								{/** Country */}
								<div className="2xl:col-span-2">
									<FormikLabel
										htmlFor="location.country"
										className="truncate"
									>
										{t(
											"profile:profile.personal_details.country.title",
										)}
									</FormikLabel>
									<label className="mt-1">
										<SelectButton className="relative lg:w-1/2">
											{(() => {
												const country =
													onlyCoreLocationCountryChoices.find(
														(c) =>
															c.code ===
															form.values.location
																?.country,
													)

												return country?.name
											})()}
											<FiChevronDown className="ml-2" />
											<Select
												onChange={(evt) => {
													evt.preventDefault()
													void form.setFieldValue(
														"location.country",
														evt.currentTarget.value,
													)
												}}
												name="location.country"
												id="location.country"
												value={
													form.values.location
														?.country ?? ""
												}
											>
												{onlyCoreLocationCountryChoices.map(
													(item) => (
														<option
															value={item.code}
															key={item.code}
														>
															{item.name}
														</option>
													),
												)}
											</Select>
										</SelectButton>
										<FormikError field="location.country" />
									</label>
								</div>

								{/** Errors */}
								<div>
									<FormikErrors i18nNamespace="profile" />
								</div>
							</div>
						</FormGroup>
					</CardBody>
					<CardFooter className="text-right">
						<FormikSubmitButton>
							{t(
								"profile:profile.company_details.form_action.save",
							)}
						</FormikSubmitButton>
					</CardFooter>
				</form>
			</CardWrapper>
		</FormikProvider>
	)
}
